import { Box, Divider, Tab, Tabs } from "@mui/material";
import Grid from "@mui/material/Grid2";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Title from "../../components/common/Title";
import { ValidationAlert } from "../../components/common/ValidationAlert";
import Layout from "../../components/layout/Layout";
import TopBarGridContainer from "../../components/layout/TopBar";
import useAlert from "../../hooks/useAlert";
import { useNatGateway } from "../../hooks/useNatGateway";
import { initScraperTask } from "../../services/aws/ecs";
import ActionButtons from "./components/ActionButtons";
import PlatformSection from "./components/PlatformSection";
import useScraperData from "./hooks/useScraperData";

/**
 * Component for creating a new task.
 *
 * @returns {JSX.Element} - The JSX element representing the form for creating a new task.
 */
function NewTask() {
  const navigate = useNavigate();
  const {
    isReady: natGatewayReady,
    isLoading: natGatewayLoading,
    checkStatus,
    createGateway,
  } = useNatGateway("scraper-natgw");
  const { companyNames, scraperVersionNames, platformOptionData, fetchData } = useScraperData("Review Platforms");
  const [taskSubmitting, setTaskSubmitting] = useState(false);
  const [selectedTaskData, setSelectedTaskData] = useState({
    database: "main",
    rerun: "true",
    scraperClass: "Review Platforms",
    selectedCompanies: [],
    scraperImage: "",
    startDate: dayjs().subtract(1, "day"),
    endDate: dayjs(),
  });
  const { alertState, showAlert, closeAlert } = useAlert();
  const platformOptionsRefs = useRef([]);

  useEffect(() => {
    fetchData();
    checkStatus();
  }, [selectedTaskData.scraperClass]);

  // Set the default scraper image to production-latest if available
  useEffect(() => {
    if (scraperVersionNames.length > 0) {
      const productionLatestVersion = scraperVersionNames.find((x) => x.val === "production-latest");

      if (productionLatestVersion) {
        setSelectedTaskData((prev) => ({
          ...prev,
          scraperImage: productionLatestVersion.key,
        }));
      }
    }
  }, [scraperVersionNames]);

  const validateInputs = () => {
    // Extract selected platforms
    const selectedPlatforms = platformOptionsRefs.current.filter((ref) => ref.current.getPlatformState().selected);

    // Validate platform selection
    if (selectedPlatforms.length === 0) {
      showAlert("error", "No Platforms Selected!");
      return false;
    }

    // Validate selected companies
    if (selectedTaskData.selectedCompanies.length === 0) {
      showAlert("error", "No Companies Selected!");
      return false;
    }

    // Validate date range
    if (selectedTaskData.startDate >= selectedTaskData.endDate) {
      showAlert("error", "Start Date Must Be Before End Date!");
      return false;
    }

    // Validate scraper version selection
    if (!selectedTaskData.scraperImage) {
      showAlert("error", "No Scraper Version Selected!");
      return false;
    }

    return true;
  };

  const handleSubmitTask = async () => {
    if (!validateInputs()) {
      return;
    }

    setTaskSubmitting(true);

    const selectedOptions = platformOptionsRefs.current
      .filter((x) => x.current.getPlatformState().selected)
      .map((x) => x.current.getPlatformState().scraperOption);

    try {
      const res = await initScraperTask(
        selectedTaskData.selectedCompanies,
        selectedTaskData.scraperImage,
        selectedTaskData.startDate.format("YYYY-MM-DD"),
        selectedTaskData.endDate.format("YYYY-MM-DD"),
        selectedTaskData.scraperClass,
        selectedOptions,
        selectedTaskData.database,
        selectedTaskData.rerun,
      );

      if (res.status === 200) {
        showAlert("success", "Task Created Successfully!");
        setTimeout(() => {
          navigate("/tasks");
        }, 1000);
      }
    } catch (error) {
      showAlert("error", "Failed to create task: " + (error.message || "Unknown error"));
      setTaskSubmitting(false);
    }
  };

  const handleCreateNatGateway = async () => {
    showAlert("info", "Creating NAT Gateway, please wait...");
    await createGateway();
  };

  const handleChangeTab = (_, val) => setSelectedTaskData((prev) => ({ ...prev, scraperClass: val }));

  return (
    <Layout>
      <ValidationAlert
        inputOpen={alertState.alertOpen}
        setInputClosed={closeAlert}
        severity={alertState.alertType}
        alertMessage={alertState.alertMsg}
      />

      <TopBarGridContainer>
        <Grid>
          <Title text={"CREATE NEW TASK"} sx={{ mt: 5 }} />
        </Grid>

        {/* Tabs for selecting platforms and action buttons*/}
        <Grid sx={{ flexGrow: 1 }}>
          <Box sx={{ display: "flex", justifyContent: "end", mt: 5 }}>
            {/* Tabs */}
            <Tabs value={selectedTaskData.scraperClass} onChange={handleChangeTab} variant="scrollable" sx={{ mx: 5 }}>
              <Tab label="Review Platforms" value="Review Platforms" />
              <Tab label="Forums" value="Forums" />
              <Tab label="Social Media" value="Social Media" />
            </Tabs>

            {/* Action buttons */}
            <ActionButtons
              onSubmit={handleSubmitTask}
              onCreateGateway={handleCreateNatGateway}
              natGatewayReady={natGatewayReady}
              natGatewayLoading={natGatewayLoading}
              taskSubmitting={taskSubmitting}
            />
          </Box>
        </Grid>
      </TopBarGridContainer>

      <Divider sx={{ my: 2 }} variant="middle" />

      {/* Platform selection section */}
      <Grid container justifyContent="space-around" height="500px" sx={{ mt: 10 }}>
        <PlatformSection
          platformOptionData={platformOptionData}
          platformOptionsRefs={platformOptionsRefs}
          selectedTaskData={selectedTaskData}
          setSelectedTaskData={setSelectedTaskData}
          companyNames={companyNames}
          scraperVersionNames={scraperVersionNames}
        />
      </Grid>
    </Layout>
  );
}

export default NewTask;
